import React, { useEffect, useState } from "react";
import { Download, Share, SquarePlus, X } from "lucide-react";
import { usePrefs } from "@/contexts/PrefsContext";

const KEY = "wn_install_dismissed";
const WEEK = 7 * 24 * 3600 * 1000;
const isIos = () => /iphone|ipad|ipod/i.test(navigator.userAgent);
const isMobile = () => /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
const installed = () => window.matchMedia("(display-mode: standalone)").matches || (navigator as any).standalone === true;

export const InstallPrompt = () => {
  const { t } = usePrefs();
  const [evt, setEvt] = useState<any>((window as any).__wnInstall || null);
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (installed() || !isMobile() || Date.now() - Number(localStorage.getItem(KEY) || 0) < WEEK) return;
    const onPrompt = (e: any) => { e.preventDefault(); (window as any).__wnInstall = e; setEvt(e); setShow(true); };
    const onInstalled = () => setShow(false);
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    const id = setTimeout(() => { if (isIos() || (window as any).__wnInstall) setShow(true); }, 2500);
    return () => { clearTimeout(id); window.removeEventListener("beforeinstallprompt", onPrompt); window.removeEventListener("appinstalled", onInstalled); };
  }, []);

  if (!show) return null;
  const close = () => { localStorage.setItem(KEY, String(Date.now())); setShow(false); };
  const install = async () => {
    if (!evt) return;
    evt.prompt();
    const { outcome } = await evt.userChoice;
    (window as any).__wnInstall = null;
    if (outcome === "accepted") setShow(false); else close();
  };

  return (
    <div data-testid="install-prompt" role="dialog" aria-label={t("inst.title")}
      className="fixed inset-x-3 bottom-[88px] z-50 rounded-3xl border bg-card/95 backdrop-blur-xl shadow-2xl p-4 animate-in slide-in-from-bottom-6 fade-in duration-500">
      <button onClick={close} data-testid="install-prompt-close" aria-label={t("common.close")} className="absolute top-3 end-3 h-8 w-8 grid place-items-center rounded-full hover:bg-accent"><X className="h-4 w-4" /></button>
      <div className="flex items-start gap-3 pe-8">
        <img src="/icon-192.png" alt="" className="h-12 w-12 rounded-2xl shadow" />
        <div className="space-y-1">
          <p className="font-display font-black">{t("inst.title")}</p>
          <p className="text-xs text-muted-foreground leading-relaxed">{t("inst.sub")}</p>
        </div>
      </div>
      {isIos() ? (
        <ol data-testid="install-prompt-ios" className="mt-3 grid gap-2 text-xs font-semibold">
          <li className="flex items-center gap-2 rounded-2xl bg-muted px-3 py-2"><span className="grid place-items-center h-6 w-6 rounded-full bg-primary text-primary-foreground text-[11px]">1</span>{t("inst.ios1")}<Share className="h-4 w-4 text-sky-500 ms-auto" /></li>
          <li className="flex items-center gap-2 rounded-2xl bg-muted px-3 py-2"><span className="grid place-items-center h-6 w-6 rounded-full bg-primary text-primary-foreground text-[11px]">2</span>{t("inst.ios2")}<SquarePlus className="h-4 w-4 ms-auto" /></li>
        </ol>
      ) : (
        <button onClick={install} data-testid="install-prompt-install" className="mt-3 w-full h-11 rounded-full bg-primary text-primary-foreground font-bold inline-flex items-center justify-center gap-2 active:scale-[0.98] transition-transform">
          <Download className="h-4 w-4" />{t("inst.install")}
        </button>
      )}
    </div>
  );
};
